import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, MenuItem, SelectChangeEvent } from "@mui/material";
import { Divider } from "@mui/material";
import images from "../../../../Assets/images";
import VcDatePicker from "Components/UI/DatePicker/DatePicker";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { scrollBar } from "Components/Common/Header/ManageOutsideClickClose";
import "../../../../Assets/css/date-picker.css";
import { useApiCall } from "Hooks/useApiCall";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import { set } from "date-fns";
import ButtonElement from "../../../UI/Button";
import "./style.css";
import ReactPaginate from "react-paginate";
import { pageOptions } from "StaticData/UserManageMent/status";
import Pagination from "Components/UI/Pagination/Pagination";
import { Padding } from "@mui/icons-material";
import Table from "../OffRoadEmissions/Table";
import { downloadOffRoadReports, getOffRoadReports } from "Network/Core/EquipmentMaster/equipment";
import { formatDate } from "Library/Utils";

interface EquipmentItem {
  month: string;
  subActivity: string;
  equipmentName: string;
  fuelType: string;
  daysOnSite: number;
  hoursOnSite: number;
  fuelEfficiencyPerHour: number;
  totalFuelUse: number;
  emissionsFactor: number;
  co2Emissions: number;
}

interface EquipmentUsageReport {
  mainActivity: string;
  supplierCompany: string;
  data: EquipmentItem[];
}
const OffRoadEmissionsReport: React.FC = () => {
  const { t } = useTranslation();
  const { call } = useApiCall();
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const inputRefS = useRef<HTMLDivElement>(null);
  const inputRefE = useRef<HTMLDivElement>(null);
  const [orderBy, setOrderBy] = useState<string>("id");
  const [statusIsASC, setStatusIsASC] = useState<boolean>(true);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [equipmentRegistryList, setEquipmentRegistryList] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [pageCount, setPageCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [startDate, setStartDate] = useState<any>("");
  const [endDate, setEndDate] = useState<any>("");
  const [companyOptions, setCompanyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [contactNamesOptions, setContactNamesOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const countryUom = getLocalStorage(ls_vars.COUNTRY_UOM);

  const headerStyle = {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      setSearchValue(searchTerm);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value.trim());
  };

  // Debounce utility function
  function debounce(func: Function, wait: number) {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  scrollBar(setIsScrolling);
  const toggleSort = (field: string, isAcs: boolean) => {
    if (field !== "id") {
      setStatusIsASC(!isAcs);
      setOrderBy(isAcs ? field : "-" + field);
    }
  };

  const handlePageClick = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const onChangePage = (value: number) => {
    setPerPage(value);
  };

  const handleDateChange = (date: Date | null, type: "start" | "end") => {
    if (type === "start") {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  // Add this state variable with the others
  const [equipmentUsageData, setEquipmentUsageData] = useState<
    EquipmentUsageReport[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEquipmentUsageReport = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const formattedStartDate = startDate
          ? moment(startDate).format("YYYY-MM-DD")
          : "";
        const formattedEndDate = endDate
          ? moment(endDate).format("YYYY-MM-DD")
          : "";

        call(
          () =>
            getOffRoadReports(
              {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                search: searchValue || undefined,
                orderBy: orderBy || undefined,

              },
              region,
              project.ref
            ),
          (response: any) => {
            const data = response?.results || response || [];
            setEquipmentUsageData(data);

          }
        );
      } catch (err) {
        setError("Failed to fetch equipment usage report");
        console.error("Error fetching equipment usage report:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (startDate && endDate && region) {
      fetchEquipmentUsageReport();
    }
  }, [startDate, endDate, region, searchValue, orderBy]);

  const exportReportData = async (reportType: string) => {
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";

    call(
      () =>
        downloadOffRoadReports(
          {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            reportType: reportType,
            search: searchValue || undefined,
            orderBy: orderBy || undefined
          },
          region,
          project.ref
        ),
      (response: any) => { }
    );
  };

  useEffect(() => {
    let today = new Date();
    let endDate = new Date();
    // Get one month after date
    endDate.setMonth(today.getMonth() + 1);
    endDate.setHours(0, 0, 0, 0);

    setStartDate(formatDate(today));
    setEndDate(formatDate(endDate));
  }, []);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <h3 className="page-title">
          {t("Translation.equipment_and_vehicle_emissions")}
        </h3>
      </div>
      <div className="page-content custom-list-div ">
        <div className="booking-list-form">
          <Box
            sx={{
              padding: "0 24px 22px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* Single row with date filters, search, and download button */}
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                alignItems: "center",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              {/* Left side with date pickers */}
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "nowrap",
                flex: "0 1 auto",
              }}>
                <Box className="date-box-frontend">
                  <VcDatePicker
                    date={startDate}
                    onDateChange={(val: Date | null) =>
                      handleDateChange(val, "start")
                    }
                  />
                </Box>
                <Box className="to" sx={{ marginBottom: 0 }}>
                  {t("Translation.to")}
                </Box>
                <Box className="date-box-frontend">
                  <VcDatePicker
                    date={endDate}
                    onDateChange={(val) => handleDateChange(val, "end")}
                    minDate={new Date(startDate || new Date())}
                  />
                </Box>
              </Box>

              {/* Middle - search box */}
              <Box sx={{
                flex: "1 1 auto",
                minWidth: "200px",
              }}>
                <div className="form-group form-inline filter-search ml-0 mr-0" style={{ marginBottom: 0 }}>
                  <img
                    src={images.search}
                    alt="search"
                    style={{
                      position: "relative",
                      left: "30px",
                      zIndex: 1,
                      height: "20px"
                    }}
                  />
                  <input
                    type="text"
                    className="form-control"
                    style={{
                      paddingLeft: "40px",
                      background: "#eff3f7",
                      borderRadius: "6px",
                      height: "40px",
                      marginBottom: "0px"
                    }}
                    placeholder={t("Translation.search_month_activity_equipment")}
                    value={searchInput}
                    onChange={handleSearchChange}
                  />
                </div>
              </Box>

              {/* Right side - download button */}
              <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                flex: "0 0 auto",
              }}>
                <ButtonElement
                  buttonLabel={<img src={images.download} alt="Download" />}
                  handleClick={() => exportReportData("or")}
                  className="btn white-btn dl-btn"
                />
              </Box>
            </Box>
          </Box>
        </div>

        <Divider />

        {Array.isArray(equipmentUsageData) && equipmentUsageData.map(
          (supplier: EquipmentUsageReport, index: number) => (
            <Table
              key={index}
              title={supplier.mainActivity ? `${supplier.mainActivity} - ${supplier.supplierCompany}` : supplier.supplierCompany}
              data={supplier.data}
              countryUom={countryUom}
              onSort={toggleSort}
              orderBy={orderBy}
              statusIsASC={statusIsASC}
            />
          )
        )}
      </div>
    </div>
  );
};

export default OffRoadEmissionsReport;
const styles = {
  headerStyle: {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
  },
  filterContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
    gap: "14px",
  },
  selectStyle: {
    width: "100%",
    height: "48px",
    background: "#EFF3F6",
    color: "#A0AEBD",
    border: "none",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  dateRangeContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "14px",
  },
  datePickerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  lastColumnWrapper: {
    display: "flex",
    justifyContent: "flex-end", // or 'flex-end' if you only want the button
    alignItems: "center",
  },
  downloadBtn: {
    border: "1px solid rgb(216 217 218)",
    borderRadius: "8px",
    cursor: "pointer",
  },
  dateRangeSeparator: {
    marginLeft: "10px",
    marginRight: "10px",
  },
  dateBoxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },
  searchImage: {
    // top: "14px" as "14px",
    position: "relative" as "relative",
    height: "20px",
    width: "20px",
    left: "30px" as "30px",
  },
  searchBox: {
    height: "45px" as "45px",
    width: "70%" as "70%",
    paddingLeft: "25px" as "25px",
    background: "#EFF3F6" as "#EEF3F6",
    border: "none" as "none",
  },
  tableHeader: {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
    "& th": {
      padding: "12px",
    },
  },
  tableRow: {
    "& td": {
      padding: "12px",
    },
  },
};
